import { DEFAULT_LOCALE } from '@constants/locales';
import { QUERY_KEY } from '@constants/queries';
import { getWhiteSpacePublicity } from '@helpers/configurations';
import {
  fetchCategoryBySlug,
  useFetchCategoryBySlug,
} from '@hooks/queries/useFetchCategoryBySlug';
import {
  fetchPublicConfiguration,
  useFetchPublicConfiguration,
} from '@hooks/queries/useFetchPublicConfiguration';
import {
  fetchPublicity,
  useFetchPublicity,
} from '@hooks/queries/useFetchPublicity';
import { Box, Grid, Stack, Title } from '@mantine/core';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import { NewsCard } from '@molecules/frontoffice/NewsCard';
import { getCategoryBySlugPath } from '@services/categories';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { validateZodSchema } from '@utils/zodSchemas';
import { categoriesDetailsPageZodSchema } from '@utils/zodSchemas/details';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { InView } from 'react-intersection-observer';

import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import {
  fetchInfiniteNewsByCategory,
  useFetchInfiniteNewsByCategory,
} from '@hooks/queries/useFetchInfiniteNewsByCategory';

export const getServerSideProps: GetServerSideProps = async ({ ...ctx }) => {
  const parsedQuery = validateZodSchema(
    categoriesDetailsPageZodSchema,
    ctx.params
  );

  const { data: parsedData } = parsedQuery;

  const params = {
    slug: parsedData?.category ?? '',
  };

  const paramsPublicity = {
    fetchAll: true,
  };

  const queryClient = new QueryClient();

  const category = await getCategoryBySlugPath(params.slug);

  if (!category) {
    return {
      notFound: true,
    };
  }

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const categoryPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORY_BY_SLUG, params.slug],
    queryFn: async () => {
      return await fetchCategoryBySlug(params.slug);
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  const publicityPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLICITY, paramsPublicity],
    queryFn: async () => {
      return await fetchPublicity(paramsPublicity);
    },
  });

  const newsPromise = queryClient.prefetchInfiniteQuery({
    queryKey: [QUERY_KEY.FETCH_NEWS_PUBLIC_BY_CATEGORY, category.id],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchInfiniteNewsByCategory({ pageParam, category: category.id }),
  });

  await Promise.all([
    categoriesPromise,
    publicityPromise,
    configurationsPromise,
    newsPromise,
    categoryPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, [
        'categories',
        'common',
      ])),
      dehydratedState: dehydrate(queryClient),
      slug: parsedData?.category ?? '',
    },
  };
};

const CategoriesPage = ({ slug }: { slug: string }) => {
  const { data: category } = useFetchCategoryBySlug(slug);

  const {
    data: infiniteNews,
    hasNextPage,
    fetchNextPage,
    isFetched,
    isFetching,
  } = useFetchInfiniteNewsByCategory(category?.id);

  const { data: publicity } = useFetchPublicity({
    fetchAll: true,
  });

  const { data: configurations } = useFetchPublicConfiguration();

  if ((isFetching && !infiniteNews) || (!isFetched && !infiniteNews)) {
    return <LoadingStateComponent />;
  }

  if (
    (isFetched && !infiniteNews) ||
    infiniteNews?.pages[0].meta.total_pages === 0 ||
    !infiniteNews ||
    !category
  ) {
    return <EmptyStateComponent />;
  }

  const hasBannerPublicity =
    publicity?.data && publicity?.data.some((pub) => pub.type === 'BANNER');

  return (
    <>
      <SEO
        title={category.title_seo}
        description={category?.description_seo}
        keywords={category.keywords_seo}
      />
      <Stack>
        {hasBannerPublicity && (
          <Box
            display="flex"
            mb={20}
            sx={(theme) => ({
              width: '100vw',
              marginLeft: 'calc(50% - 50vw)',
              [`@media (min-width: ${theme.breakpoints.md})`]: {
                width: '100%',
                marginLeft: 0,
              },
            })}
          >
            <RandomPublicityComponent
              index={0}
              type="BANNER"
              publicities={publicity?.data ?? []}
            />
          </Box>
        )}
        <Title variant="h1Orange" mb={20}>
          {category?.name}
        </Title>
        {infiniteNews?.pages.map((newsPage, index) => (
          <Grid key={index} columns={4} gutter={40}>
            {newsPage?.data.map((newsData, newsIndex) => (
              <Grid.Col key={newsData.id} lg={1}>
                {newsPage.data.length - 1 === newsIndex ? (
                  <InView
                    as="div"
                    style={{ padding: 0, margin: 0 }}
                    onChange={(inView) => {
                      if (
                        hasNextPage &&
                        inView &&
                        infiniteNews?.pages.length - 1 === index
                      ) {
                        fetchNextPage();
                      }
                    }}
                    key={index}
                    threshold={0.8}
                  >
                    <NewsCard
                      news={newsData}
                      titleOrder={1}
                      hasBadge
                      titleVariant="h3Dark"
                      hasSubtitle
                      hasDescription={false}
                    />
                  </InView>
                ) : (
                  <NewsCard
                    news={newsData}
                    titleOrder={1}
                    hasBadge
                    titleVariant="h3Dark"
                    hasSubtitle
                    hasDescription={false}
                  />
                )}
              </Grid.Col>
            ))}
            <Grid.Col lg={1}>
              <Box>
                {index % getWhiteSpacePublicity(configurations) === 0 && (
                  <RandomPublicityComponent
                    index={Math.ceil(
                      index / getWhiteSpacePublicity(configurations)
                    )}
                    type="SIDEBAR"
                    publicities={publicity?.data ?? []}
                  />
                )}
              </Box>
            </Grid.Col>
          </Grid>
        ))}
      </Stack>
    </>
  );
};

export default CategoriesPage;
